/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-non-null-assertion */
import React, {
  useEffect, useState, forwardRef, useImperativeHandle, useRef, useCallback, memo,
} from 'react';
import { Spin, Checkbox, InputNumber } from 'antd';
import { FormikValues, useFormik } from 'formik';
import * as Yup from 'yup';
// eslint-disable-next-line import/no-extraneous-dependencies
import { fabric } from 'fabric';
import { useActions } from '../../providers/actions-provider/actions.context';
import { useStore } from '../../../store';
import DeleteModal from '../../lib/delete-modal';
import Canvas from './ZoneCanvas';
import Form from './ZoneForm';
import './zoneMaker.less';
import {
  IZoneFormValue, IExistingZone, IZoneFormValues, IDrawnPolygons,
} from './ZoneMaker.types';
import Slider from './ZoneTransparencySlider';
import { useLocalStorage } from '../../providers/local-storage-provider/local-storage.context';
import {
  polygonPositionHandler, actionHandler, anchorWrapper, isRectangleShape, setShapeEditingMode,
} from './polygonHelpers';
import LeavePageModal from '../../lib/leave-page-modal';
import { zoneNameRule } from './zoneName.validation';

const buildZoneMakerValidationRules = () => Yup.object().shape({
  zones: Yup.array().of(
    Yup.object().shape({
      name: zoneNameRule,
    }),
  ),
});

export interface IZoneMakerProps {
  groupId: string;
  // eslint-disable-next-line
  fieldArrayRef: React.RefObject<any>;
  setDisableSave: (val: boolean) => void;
  existingZones: IExistingZone[];
  hasUnsavedChanges: boolean;
  // eslint-disable-next-line react/no-unused-prop-types
  zonesContainerRef: React.RefObject<any>;
  currentZonesFilter: string;
  searchZonesValue: string;
}

export interface IZoneMakerRef {
  submitForm: () => Promise<void>;
}

interface IDragState {
  points: fabric.Point[];
  left: number;
  top: number;
  pathOffset: fabric.Point;
}

const ZoneMaker = forwardRef<IZoneMakerRef, IZoneMakerProps>((props, ref) => {
  const {
    groupId,
    fieldArrayRef,
    setDisableSave,
    existingZones,
    hasUnsavedChanges,
    zonesContainerRef,
    currentZonesFilter,
    searchZonesValue,
  } = props;
  const { facilityActions, tenantActions } = useActions();
  const { mapImageImage, mapImage } = useStore();
  const localStorageService = useLocalStorage();
  const transparency = Number(localStorageService.get('ZONE_MAKER_TRANSPARENCY')) > 0.9 ? 0.9 : Number(localStorageService.get('ZONE_MAKER_TRANSPARENCY'));
  const gridStepValue = Number(localStorageService.get('ZONE_MAKER_GRID_STEP'));
  const snapToGridValue = Boolean(localStorageService.get('ZONE_MAKER_SNAP_TO_GRID'));

  const imageLoaded = mapImageImage.data instanceof Blob;

  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<number | null>(null);
  const [canvas, setCanvas] = useState<fabric.Canvas | null>(null);
  const [isDrawing, setIsDrawing] = useState<boolean>(false);
  const [isInsertingRectangle, setIsInsertingRectangle] = useState<boolean>(false);
  const [currentPoints, setCurrentPoints] = useState<fabric.Point[]>([]);
  const [tempLines, setTempLines] = useState<fabric.Line[]>([]);
  const [tempCircles, setTempCircles] = useState<fabric.Circle[]>([]);
  const [activeZoneIndex, setActiveZoneIndex] = useState<number | null>(null);
  const [drawnPolygons, setDrawnPolygons] = useState<IDrawnPolygons>({});
  const [imageTransparency, setImageTransparency] = useState<number>(transparency || 0.5);
  const [initialDragState, setInitialDragState] = useState<IDragState| null>(null);
  const [editingZoneIndex, setEditingZoneIndex] = useState<number | null>(null);
  const [isPanning, setIsPanning] = useState(false);
  const [snapToGrid, setSnapToGrid] = useState<boolean>(snapToGridValue);
  const [gridStep, setGridStep] = useState<number>(gridStepValue || 20);

  const lastMousePosition = useRef({ x: 0, y: 0 });
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [imageDimensions, setImageDimensions] = useState<{
    width: number; height: number;
  } | null>(null);

  const insertLeavePageModal = hasUnsavedChanges
    || activeZoneIndex !== null
    || editingZoneIndex !== null;

  /**
   * Converts fabric canvas coordinates to range coordinates.
   */
  const canvasToRangeCoordinates = useCallback(
    (canvasX: number, canvasY: number): [number, number] => {
      if (!canvas || !imageDimensions || !mapImage.data.pixelsPerLength) {
        console.warn('Missing required data for coordinate conversion');
        return [canvasX, canvasY];
      }

      const { xofffset, yoffset, pixelsPerLength } = mapImage.data;

      // Scale factor between display canvas and actual image dimensions
      const scaleX = imageDimensions.width / canvas.width!;
      const scaleY = imageDimensions.height / canvas.height!;

      // Convert canvas coordinates to actual image pixel coordinates
      const imagePixelX = canvasX * scaleX;
      const imagePixelY = canvasY * scaleY;

      // Convert pixels to physical units, accounting for origin offset
      // The origin point is at (xofffset, yoffset) in the image
      // rangeY is flipped
      const rangeX = (imagePixelX - Number(xofffset)) / Number(pixelsPerLength);
      const rangeY = (imageDimensions.height - imagePixelY - Number(yoffset))
      / Number(pixelsPerLength);

      return [rangeX, rangeY];
    }, [canvas, imageDimensions, mapImage.data],
  );

  /**
   * Converts range coordinates to fabric canvas coordinates.
   */
  const rangeToCanvasCoordinates = useCallback((rangeX: number, rangeY: number): fabric.Point => {
    if (!canvas || !imageDimensions || !mapImage.data.pixelsPerLength) {
      console.warn('Missing required data for coordinate conversion');
      return new fabric.Point(0, 0);
    }

    const { xofffset, yoffset, pixelsPerLength } = mapImage.data;

    const imagePixelX = rangeX * Number(pixelsPerLength) + Number(xofffset);
    const imagePixelY = imageDimensions.height - (rangeY * Number(pixelsPerLength)
      + Number(yoffset));

    const scaleX = canvas.width! / imageDimensions.width;
    const scaleY = canvas.height! / imageDimensions.height;

    const canvasX = imagePixelX * scaleX;
    const canvasY = imagePixelY * scaleY;

    return new fabric.Point(canvasX, canvasY);
  }, [canvas, imageDimensions, mapImage.data]);

  const calculateCentroid = (points: fabric.Point[]) => {
    let x = 0;
    let y = 0;
    points.forEach((point) => {
      x += point.x;
      y += point.y;
    });
    return {
      x: x / points.length,
      y: y / points.length,
    };
  };

  const createZoomIndependentText = (
    text: string, left: number, top: number, fabricCanvas: fabric.Canvas,
  ) => {
    const zoom = fabricCanvas.getZoom();
    return new fabric.Text(text, {
      left,
      top,
      fontSize: 24 / zoom,
      fill: '#fff',
      backgroundColor: '#4CAF50',
      padding: 5 / zoom,
      originX: 'center',
      originY: 'center',
      selectable: false,
      evented: false,
    });
  };

  const deleteZonePolygon = useCallback((index: number) => {
    if (!canvas) return;

    const polygonData = drawnPolygons[index];
    if (polygonData) {
      if (polygonData?.polygon) canvas.remove(polygonData.polygon);
      if (polygonData?.text) canvas.remove(polygonData.text);
      canvas.renderAll();

      setDrawnPolygons((prevPolygons) => {
        const updated = { ...prevPolygons };
        delete updated[index];
        return updated;
      });

      setDisableSave(false);
    }
  }, [canvas, drawnPolygons]);

  const cancelDrawing = useCallback(() => {
    if (!canvas) return;

    setTempLines((lines) => {
      lines.forEach((line) => canvas.remove(line));
      return [];
    });

    setTempCircles((circles) => {
      circles.forEach((circle) => canvas.remove(circle));
      return [];
    });

    canvas.renderAll();

    setCurrentPoints([]);
    setIsDrawing(false);
    setIsInsertingRectangle(false);
    setActiveZoneIndex(null);
  }, [canvas]);

  const closePolygon = useCallback(() => {
    if (!canvas || currentPoints.length < 3 || activeZoneIndex === null) return;

    tempLines.forEach((line) => canvas.remove(line));
    tempCircles.forEach((circle) => canvas.remove(circle));
    setTempLines([]);
    setTempCircles([]);

    const polygon = new fabric.Polygon(currentPoints, {
      fill: 'rgba(76, 175, 80, 0.3)',
      stroke: '#4CAF50',
      strokeWidth: 2,
      selectable: false,
      evented: false,
      objectCaching: false,
      perPixelTargetFind: true,
      strokeUniform: true,
    });

    const centroid = calculateCentroid(currentPoints);
    const text = createZoomIndependentText(`${activeZoneIndex + 1}`, centroid.x, centroid.y, canvas);

    canvas.add(polygon);
    canvas.add(text);
    canvas.renderAll();

    setDrawnPolygons((prev) => ({
      ...prev,
      [activeZoneIndex]: { polygon, text },
    }));

    setIsDrawing(false);
    setCurrentPoints([]);
    setActiveZoneIndex(null);
    setDisableSave(false);
  }, [canvas, currentPoints, activeZoneIndex, tempLines, tempCircles]);

  const snapValue = useCallback((val: number) => Math.round(val / gridStep) * gridStep, [gridStep]);

  const handleCanvasClick = useCallback((e: fabric.IEvent) => {
    if (!canvas || !isDrawing || activeZoneIndex === null) return;

    const pointer = canvas.getPointer(e.e);
    let pointX = pointer.x;
    let pointY = pointer.y;

    if (snapToGrid) {
      pointX = snapValue(pointX);
      pointY = snapValue(pointY);
    }
    const point = new fabric.Point(pointX, pointY);

    if (currentPoints.length >= 3) {
      const firstPoint = currentPoints[0];
      const distance = Math.sqrt(
        // eslint-disable-next-line no-restricted-properties
        Math.pow(point.x - firstPoint.x, 2) + Math.pow(point.y - firstPoint.y, 2),
      );
      if (distance < 5) {
        closePolygon();
        return;
      }
    }

    const newPoints = [...currentPoints, point];
    const zoom = canvas.getZoom();

    const circle = new fabric.Circle({
      radius: 5 / zoom,
      fill: '#4CAF50',
      left: point.x,
      top: point.y,
      originX: 'center',
      originY: 'center',
      selectable: false,
      evented: false,
    });

    canvas.add(circle);

    if (newPoints.length > 1) {
      const prevPoint = newPoints[newPoints.length - 2];
      const line = new fabric.Line(
        [prevPoint.x, prevPoint.y, point.x, point.y],
        {
          stroke: '#4CAF50',
          strokeWidth: 2 / zoom,
          selectable: false,
          evented: false,
        },
      );
      canvas.add(line);
      setTempLines((prev) => [...prev, line]);
    }

    setCurrentPoints(newPoints);
    setTempCircles((prev) => [...prev, circle]);

    canvas.renderAll();
  }, [canvas, isDrawing, activeZoneIndex, currentPoints, closePolygon, snapToGrid, snapValue]);

  const undoLastPoint = useCallback(() => {
    if (!canvas || currentPoints.length === 0 || !isDrawing) return;

    setCurrentPoints((prevPoints) => prevPoints.slice(0, -1));

    setTempCircles((prevCircles) => {
      const lastCircle = prevCircles[prevCircles.length - 1];
      canvas.remove(lastCircle);
      return prevCircles.slice(0, -1);
    });

    setTempLines((prevLines) => {
      if (prevLines.length) {
        const lastLine = prevLines[prevLines.length - 1];
        canvas.remove(lastLine);
        return prevLines.slice(0, -1);
      }
      return prevLines;
    });

    canvas.renderAll();
  }, [canvas, currentPoints.length, isDrawing]);

  const formik = useFormik<IZoneFormValues>({
    initialValues: {
      zones: existingZones.map((zone) => ({
        id: zone.id,
        name: zone.name,
        propertiesAsText: zone.propertiesAsText,
        logicalReadersAsText: zone.logicalReadersAsText,
        polygonsAsText: zone.polygonsAsText,
      })),
    },
    validationSchema: buildZoneMakerValidationRules,
    onSubmit: async (values: FormikValues) => {
      const zonesWithCoordinates = values.zones.map((zone: IZoneFormValue, index: number) => {
        const polygonData = drawnPolygons[index];
        if (polygonData?.polygon) {
          const points = polygonData.polygon.points || [];
          // Convert canvas coordinates to range coordinates
          const coordinates = points.map(
            (point: { x: number; y: number; }) => {
              const [rangeX, rangeY] = canvasToRangeCoordinates(point.x, point.y);
              return [Number(rangeX.toFixed(1)), Number(rangeY.toFixed(1))];
            },
          );
          return {
            ...zone,
            polygonsAsText: JSON.stringify(coordinates),
          };
        }
        return {
          ...zone,
          polygonsAsText: '',
        };
      });

      await tenantActions.saveZones(
        zonesWithCoordinates,
        +groupId,
      );

      formik.resetForm({
        values: { zones: [] },
      });
      setDrawnPolygons({});
      if (canvas) {
        canvas.getObjects().forEach((obj) => canvas.remove(obj));
        canvas.renderAll();
      }
      await facilityActions.getZones(groupId);
    },
    enableReinitialize: true,
  });

  const { errors, values } = formik;

  const startDrawing = useCallback((index: number) => {
    if (drawnPolygons[index]?.polygon) {
      // eslint-disable-next-line no-alert
      alert('This zone already has a polygon drawn. Delete it first to redraw.');
      return;
    }
    setIsDrawing(true);
    setActiveZoneIndex(index);
    setCurrentPoints([]);
  }, [drawnPolygons]);

  const startInsertingRectangle = useCallback((index: number) => {
    if (!canvas || drawnPolygons[index]?.polygon) {
      // eslint-disable-next-line no-alert
      alert('This zone already has a polygon drawn. Delete it first to redraw.');
      return;
    }

    cancelDrawing();
    setIsInsertingRectangle(true);
    setActiveZoneIndex(index);

    const rect = new fabric.Rect({
      left: canvas.width! / 2,
      top: canvas.height! / 2,
      width: 50,
      height: 50,
      fill: 'rgba(33, 150, 243, 0.3)',
      stroke: '#2196F3',
      strokeWidth: 2,
      originX: 'center',
      originY: 'center',
      lockScalingFlip: true,
      uniformScaling: true,
    } as fabric.IObjectOptions);

    canvas.add(rect);
    canvas.setActiveObject(rect);

    setDrawnPolygons((prev) => ({
      ...prev,
      [index]: { polygon: rect as any, text: null },
    }));

    setEditingZoneIndex(index);
    setDisableSave(true);
    canvas.requestRenderAll();
  }, [canvas, drawnPolygons, cancelDrawing]);

  const cancelInsertingRectangle = useCallback((index: number) => {
    if (!canvas || typeof index !== 'number') return;

    const rect = drawnPolygons[index]?.polygon;
    if (rect?.type === 'rect') {
      canvas.remove(rect);
      setDrawnPolygons((prevPolygons) => {
        const updated = { ...prevPolygons };
        delete updated[index];
        return updated;
      });
    }

    setEditingZoneIndex(null);
    setIsInsertingRectangle(false);
    setInitialDragState(null);
    setDisableSave(false);

    canvas.discardActiveObject();
    canvas.requestRenderAll();
  }, [canvas, drawnPolygons]);

  const saveInsertingRectangle = useCallback((index: number) => {
    if (!canvas || typeof index !== 'number') return;

    const rect = drawnPolygons[index]?.polygon;
    if (!rect || rect.type !== 'rect') return;

    const width = rect.width! * rect.scaleX!;
    const height = rect.height! * rect.scaleY!;
    const left = rect.left!;
    const top = rect.top!;

    const halfWidth = width / 2;
    const halfHeight = height / 2;

    const corners = [
      new fabric.Point(left - halfWidth, top - halfHeight),
      new fabric.Point(left + halfWidth, top - halfHeight),
      new fabric.Point(left + halfWidth, top + halfHeight),
      new fabric.Point(left - halfWidth, top + halfHeight),
    ];

    const angle = rect.angle || 0;
    const absolutePoints = corners.map((corner) => {
      if (angle === 0) return corner;
      return fabric.util.rotatePoint(
        corner, new fabric.Point(left, top), fabric.util.degreesToRadians(angle),
      );
    });

    const snappedAbsolutePoints = snapToGrid
      ? absolutePoints.map((p) => new fabric.Point(snapValue(p.x), snapValue(p.y)))
      : absolutePoints;

    const zoneName = drawnPolygons[index]?.text?.text;
    const centroid = calculateCentroid(snappedAbsolutePoints);
    const text = createZoomIndependentText(zoneName || `${index + 1}`, centroid.x, centroid.y, canvas);

    const updatedRect = new fabric.Polygon(snappedAbsolutePoints, {
      fill: 'rgba(76, 175, 80, 0.3)',
      stroke: '#4CAF50',
      strokeWidth: 2,
      selectable: false,
      evented: false,
    });

    canvas.remove(rect);
    canvas.add(updatedRect);
    canvas.add(text);

    setDrawnPolygons((prev) => ({
      ...prev,
      [index]: {
        polygon: updatedRect,
        text,
      },
    }));

    canvas.discardActiveObject();
    canvas.requestRenderAll();

    formik.setFieldTouched(`zones.${index}.polygonsAsText`, true, false);

    setEditingZoneIndex(null);
    setIsInsertingRectangle(false);
    setIsDrawing(false);
    setDisableSave(false);
  }, [canvas, drawnPolygons, calculateCentroid, formik, snapToGrid, snapValue]);

  const getFilteredZoneIndices = useCallback(() => {
    if (!values.zones) return new Set();

    const filteredIndices = values.zones
      .map((zone, index) => ({ ...zone, originalIndex: index }))
      .filter((zone) => {
        if (!currentZonesFilter) return true;
        const key = currentZonesFilter.toLowerCase();
        const value = (zone as any)[key];
        return value?.toLowerCase().includes(searchZonesValue.toLowerCase());
      })
      .map((zone) => zone.originalIndex);

    return new Set(filteredIndices);
  }, [values.zones, currentZonesFilter, searchZonesValue]);

  const handleZoneDelete = useCallback(() => {
    if (!canvas || typeof deleteTarget !== 'number') return;
    if (deleteTarget === activeZoneIndex) {
      cancelDrawing();
    }
    deleteZonePolygon(deleteTarget);
    formik.setFieldValue(
      'zones',
      values.zones.filter((_, i) => i !== deleteTarget),
    );
    setDrawnPolygons((prevPolygons) => {
      const reindexed: typeof prevPolygons = {};
      Object.keys(prevPolygons).forEach((key) => {
        const oldIndex = +key;
        if (oldIndex === deleteTarget) return;
        const newIndex = oldIndex > deleteTarget ? oldIndex - 1 : oldIndex;
        reindexed[newIndex] = prevPolygons[oldIndex];
        reindexed[newIndex].text?.set('text', (newIndex + 1).toString());
      });
      return reindexed;
    });
    canvas.renderAll();
    setDeleteModalVisible(false);
    setDeleteTarget(null);
    formik.setTouched({
      ...formik.touched,
      zones: [{ name: true }],
    });
  }, [canvas, deleteTarget, activeZoneIndex, deleteZonePolygon, formik]);

  const handleImageTransparency = useCallback((value: number) => {
    setImageTransparency(value);
    localStorageService.set('ZONE_MAKER_TRANSPARENCY', value);
  }, [localStorageService]);

  const enablePolygonEditing = useCallback((index: number) => {
    if (!canvas) return;

    const poly = drawnPolygons[index]?.polygon as fabric.Polygon;
    const text = drawnPolygons[index]?.text as fabric.Text;
    if (!poly) return;

    const isRect = isRectangleShape(poly);
    setShapeEditingMode(poly, isRect);

    const originalPoints = poly.points!.map((p) => new fabric.Point(p.x, p.y));

    setInitialDragState({
      points: originalPoints,
      left: poly.left ?? 0,
      top: poly.top ?? 0,
      pathOffset: new fabric.Point(poly.pathOffset.x, poly.pathOffset.y),
    });

    cancelDrawing?.();

    (poly as any).edit = true;

    poly.set({
      selectable: true,
      evented: true,
      hasControls: true,
      hasBorders: false,
      objectCaching: false,
      cornerStyle: 'circle',
      cornerColor: 'rgba(0,0,255,0.5)',
      fill: 'rgba(33, 150, 243, 0.3)',
      stroke: '#2196F3',
      strokeUniform: true,
      perPixelTargetFind: true,
    });

    (poly as any).snapToGrid = snapToGrid;
    (poly as any).gridStep = gridStep;

    if (text) text.set({ visible: false });

    canvas.setActiveObject(poly);
    canvas.requestRenderAll();

    setEditingZoneIndex(index);
    setDisableSave(true);
  }, [canvas, drawnPolygons, cancelDrawing, setDisableSave, gridStep, snapToGrid]);

  const cancelPolygonEditing = useCallback((index: number) => {
    if (!canvas || index === null) return;

    const poly = drawnPolygons[index]?.polygon as fabric.Polygon;
    const text = drawnPolygons[index]?.text as fabric.Text;

    if (!poly || !initialDragState) return;

    poly.set({
      points: initialDragState.points,
      left: initialDragState.left,
      top: initialDragState.top,
      pathOffset: initialDragState.pathOffset,
      fill: 'rgba(76, 175, 80, 0.3)',
      stroke: '#4CAF50',
      selectable: false,
      evented: false,
      hasControls: false,
      objectCaching: true,
    });
    poly.setCoords();

    if (text) {
      const centroid = calculateCentroid(initialDragState.points);
      text.set({
        left: centroid.x,
        top: centroid.y,
        selectable: false,
        visible: true,
        backgroundColor: '#4CAF50',
      });
      text.setCoords();
    }

    (poly as any).edit = false;
    poly.controls = fabric.Object.prototype.controls;

    setEditingZoneIndex(null);
    setInitialDragState(null);
    setDisableSave(false);

    canvas.discardActiveObject();
    canvas.requestRenderAll();
  }, [canvas, drawnPolygons, initialDragState, calculateCentroid, setDisableSave]);

  const savePolygonEdits = useCallback((index: number) => {
    if (!canvas || typeof index !== 'number') return;

    const poly = drawnPolygons[index]?.polygon;
    const text = drawnPolygons[index]?.text;
    if (!poly) return;

    const absolutePoints = poly.points!.map((p) => fabric.util.transformPoint(
      new fabric.Point(p.x - poly.pathOffset.x, p.y - poly.pathOffset.y),
      poly.calcTransformMatrix(),
    ));

    const snappedAbsolutePoints = snapToGrid
      ? absolutePoints.map((p) => new fabric.Point(snapValue(p.x), snapValue(p.y)))
      : absolutePoints;

    const centroid = calculateCentroid(snappedAbsolutePoints);
    if (text) {
      text.set({
        left: centroid.x,
        top: centroid.y,
        visible: true,
        selectable: false,
        backgroundColor: '#4CAF50',
      });
      text.setCoords();
    }

    const updatedPolygon = new fabric.Polygon(snappedAbsolutePoints, {
      fill: 'rgba(76, 175, 80, 0.3)',
      stroke: '#4CAF50',
      strokeWidth: 2,
      selectable: false,
      evented: false,
    });

    canvas.remove(poly);
    canvas.add(updatedPolygon);

    setDrawnPolygons((prev) => ({
      ...prev,
      [index]: {
        polygon: updatedPolygon,
        text,
      },
    }));

    canvas.discardActiveObject();
    canvas.requestRenderAll();

    formik.setFieldTouched(`zones.${index}.polygonsAsText`, true, false);

    setEditingZoneIndex(null);
    setDisableSave(false);
  }, [canvas, drawnPolygons, calculateCentroid, formik, setDisableSave, snapToGrid, snapValue]);

  useImperativeHandle(ref, () => ({
    submitForm: async () => {
      await formik.submitForm();
    },
  }), [formik.submitForm]);

  const handleZoom = useCallback((opt: fabric.IEvent) => {
    if (!canvas) return;

    const e = opt.e as WheelEvent;
    e.preventDefault();
    e.stopPropagation();

    const isPinchZoom = e.ctrlKey || e.metaKey;
    let zoom = canvas.getZoom();

    if (isPinchZoom) {
      zoom *= 0.999 ** e.deltaY;
    } else {
      zoom *= 1.1 ** (-e.deltaY / 100);
    }

    zoom = Math.min(Math.max(0.01, zoom), 20);
    const point = new fabric.Point(e.offsetX, e.offsetY);
    canvas.zoomToPoint(point, zoom);
  }, [canvas]);

  const handleMouseDown = useCallback((opt: fabric.IEvent) => {
    if (!canvas || isDrawing || editingZoneIndex !== null) return;

    const e = opt.e as WheelEvent;
    setIsPanning(true);
    lastMousePosition.current = { x: e.clientX, y: e.clientY };
    canvas.selection = false;
    canvas.defaultCursor = 'grab';
  }, [canvas, isDrawing, editingZoneIndex]);

  const handleMouseMove = useCallback((opt: fabric.IEvent) => {
    if (!canvas || !isPanning) return;

    const e = opt.e as WheelEvent;
    const vpt = canvas.viewportTransform;

    vpt![4] += e.clientX - lastMousePosition.current.x;
    vpt![5] += e.clientY - lastMousePosition.current.y;

    canvas.requestRenderAll();

    lastMousePosition.current = { x: e.clientX, y: e.clientY };
  }, [canvas, isPanning]);

  const handleMouseUp = useCallback(() => {
    if (!canvas || !isPanning) return;

    if (canvas.viewportTransform) {
      canvas.setViewportTransform(canvas.viewportTransform);
    }

    setIsPanning(false);
    canvas.selection = true;
    canvas.defaultCursor = isDrawing ? 'crosshair' : 'default';
  }, [canvas, isPanning, isDrawing]);

  const handlePolygonColorChange = useCallback((index: number) => {
    if (!canvas) return;

    Object.entries(drawnPolygons).forEach(([key, { polygon, text }]) => {
      if (!polygon) return;
      const i = Number(key);
      if (i === index) {
        const isSelected = polygon.stroke === '#3d1cb0';

        polygon.set({
          stroke: isSelected ? '#4CAF50' : '#3d1cb0',
          fill: isSelected ? 'rgba(76, 175, 80, 0.3)' : 'rgba(61, 28, 176, 0.3)',
        });

        text.set({
          backgroundColor: isSelected ? '#4CAF50' : '#3d1cb0',
          fill: '#fff',
        });
      } else {
        const isSelected = polygon.stroke === '#3d1cb0';

        if (isSelected) {
          polygon.set({
            stroke: '#4CAF50',
            fill: 'rgba(76, 175, 80, 0.3)',
          });

          text.set({
            backgroundColor: '#4CAF50',
            fill: '#fff',
          });
        }
      }
    });

    canvas.renderAll();
  }, [drawnPolygons, canvas]);

  // Snap to grid when editing
  useEffect(() => {
    if (!canvas) return undefined;

    const handleObjectModified = (opts: fabric.IEvent) => {
      const target = opts.target as any;
      if (!target) return;

      if (target.type === 'polygon' && (target as any).edit) {
        const foundIndex = Object.keys(drawnPolygons)
          .find((k) => drawnPolygons[+k]?.polygon === target);
        if (typeof foundIndex === 'undefined') return;
        const index = Number(foundIndex);

        if (snapToGrid && opts.transform?.action === 'drag') {
          const snappedLeft = snapValue(target.left);
          const snappedTop = snapValue(target.top);

          target.set({
            left: snappedLeft,
            top: snappedTop,
          });
          target.setCoords();

          const text = drawnPolygons[index]?.text;
          if (text) {
            const absolutePoints = target.points!.map((p: any) => fabric.util.transformPoint(
              new fabric.Point(p.x - target.pathOffset.x, p.y - target.pathOffset.y),
              target.calcTransformMatrix(),
            ));
            const centroid = calculateCentroid(absolutePoints);
            text.set({ left: centroid.x, top: centroid.y });
            text.setCoords();
          }

          canvas.requestRenderAll();
        }

        formik.setFieldTouched(`zones.${index}.polygonsAsText`, true, false);
      }
    };

    canvas.on('object:modified', handleObjectModified);

    return () => {
      canvas.off('object:modified', handleObjectModified);
    };
  }, [canvas, drawnPolygons, snapToGrid, snapValue, calculateCentroid, formik]);

  // Control save changes button enabled/disabled state
  useEffect(() => {
    if (
      (!values.zones?.length && !existingZones.length)
      || (errors.zones?.length ?? 0) > 0
      || !formik.touched.zones?.length) {
      setDisableSave(true);
    } else if (formik.touched.zones?.length) {
      setDisableSave(false);
    }
  }, [values.zones, formik.touched.zones, errors.zones]);

  // Refresh text inside polygons
  useEffect(() => {
    if (!canvas) return;

    values.zones.forEach((zone, index) => {
      const polygonData = drawnPolygons[index];
      if (polygonData?.text) {
        polygonData.text.set('text', zone.name || (index + 1).toString());
        canvas.renderAll();
      }
    });
  }, [values.zones, drawnPolygons]);

  // Draw existing polygons
  useEffect(() => {
    if (!canvas || !imageDimensions || !mapImage.data.pixelsPerLength) return;

    const initialDrawnPolygons: IDrawnPolygons = {};

    existingZones.forEach((zone, index) => {
      if (zone.polygonsAsText) {
        try {
          const coordinates: [number, number][] = JSON.parse(zone.polygonsAsText);
          const points: fabric.Point[] = coordinates
            .map(([rangeX, rangeY]) => rangeToCanvasCoordinates(rangeX, rangeY));

          if (points.length >= 3) {
            const polygon = new fabric.Polygon(points, {
              fill: 'rgba(76, 175, 80, 0.3)',
              stroke: '#4CAF50',
              strokeWidth: 2,
              selectable: false,
              evented: false,
            });
            const centroid = calculateCentroid(points);
            const text = new fabric.Text(zone.name || `${index + 1}`, {
              left: centroid.x,
              top: centroid.y,
              fontSize: 24,
              fill: '#fff',
              backgroundColor: '#4CAF50',
              padding: 5,
              originX: 'center',
              originY: 'center',
              selectable: false,
              evented: false,
            });
            canvas.add(polygon);
            canvas.add(text);
            initialDrawnPolygons[index] = { polygon, text };
          }
        } catch (error) {
          console.error(`Error parsing polygon for zone ${zone.name}:`, error);
        }
      }
    });

    setDrawnPolygons((prev) => ({
      ...prev,
      ...initialDrawnPolygons,
    }));

    canvas.renderAll();

    // eslint-disable-next-line consistent-return
    return () => {
      if (!Object.keys(initialDrawnPolygons).length) return;

      Object.values(initialDrawnPolygons).forEach(({ polygon, text }) => {
        if (polygon) canvas.remove(polygon);
        if (text) canvas.remove(text);
      });
      canvas.renderAll();
    };
  }, [canvas, imageDimensions, mapImage.data, existingZones, rangeToCanvasCoordinates]);

  // Prevent polygon deselection when clicked outside during edit
  useEffect(() => {
    if (!canvas) return;

    const handleBeforeSelectionCleared = (e: fabric.IEvent) => {
      if (editingZoneIndex !== null) {
        const poly = drawnPolygons[editingZoneIndex]?.polygon;
        if (!poly) return;

        if (e.target === poly || e.target === null) {
          if (e.e && typeof e.e.preventDefault === 'function') {
            e.e.preventDefault();
          }
        }
      }
    };

    canvas.on('before:selection:cleared', handleBeforeSelectionCleared);

    // eslint-disable-next-line consistent-return
    return () => {
      canvas.off('before:selection:cleared', handleBeforeSelectionCleared);
    };
  }, [canvas, editingZoneIndex, drawnPolygons]);

  // Canvas cursor style
  useEffect(() => {
    if (canvas) {
      canvas.hoverCursor = isDrawing ? 'crosshair' : 'default';
      canvas.defaultCursor = isDrawing ? 'crosshair' : 'default';
      canvas.renderAll();
    }
  }, [canvas, isDrawing]);

  // Zoom and Pan listeners
  useEffect(() => {
    if (!canvas) return;

    canvas.on('mouse:wheel', handleZoom);
    canvas.on('mouse:down', handleMouseDown);
    canvas.on('mouse:move', handleMouseMove);
    canvas.on('mouse:up', handleMouseUp);
    canvas.on('mouse:out', handleMouseUp);

    // eslint-disable-next-line consistent-return
    return () => {
      canvas.off('mouse:wheel', handleZoom);
      canvas.off('mouse:down', handleMouseDown);
      canvas.off('mouse:move', handleMouseMove);
      canvas.off('mouse:up', handleMouseUp);
      canvas.off('mouse:out', handleMouseUp);
    };
  }, [canvas, handleZoom, handleMouseDown, handleMouseMove, handleMouseUp]);

  useEffect(() => {
    if (!canvas) return;

    Object.values(drawnPolygons).forEach(({ polygon }) => {
      if (polygon) {
        const poly = polygon as any;
        poly.snapToGrid = snapToGrid;
        poly.gridStep = gridStep;
      }
    });
  }, [snapToGrid, gridStep, drawnPolygons, canvas]);

  // Reverse zoom zone lines and circles
  useEffect(() => {
    if (!canvas) return;

    const handleZoomed = () => {
      const zoom = canvas.getZoom();
      Object.values(drawnPolygons).forEach(({ text }) => {
        if (text) {
          text.set({
            fontSize: 24 / zoom,
            padding: 5 / zoom,
          });
          text.setCoords();
        }
      });
      tempCircles.forEach((circle) => circle.set({ radius: 5 / zoom }));
      tempLines.forEach((line) => line.set({ strokeWidth: 2 / zoom }));
      canvas.requestRenderAll();
    };

    canvas.on('after:render', handleZoomed);

    // eslint-disable-next-line consistent-return
    return () => {
      canvas.off('after:render', handleZoomed);
    };
  }, [canvas, tempCircles, tempLines, drawnPolygons]);

  // Show filtered polygons
  useEffect(() => {
    if (!canvas) return;

    const indicesToDisplay = getFilteredZoneIndices();

    Object.entries(drawnPolygons).forEach(([key, { polygon, text }]) => {
      const index = Number(key);
      const shouldBeVisible = indicesToDisplay.has(index);

      if (polygon) {
        polygon.set({
          visible: shouldBeVisible,
        });
      }
      if (text) {
        text.set({
          visible: shouldBeVisible,
        });
      }
    });
    canvas.renderAll();
  }, [canvas, drawnPolygons, getFilteredZoneIndices, values.zones.length]);

  return (
    <>
      <Spin spinning={!imageLoaded} size="default" className="zone-maker-spinner" />
      { imageLoaded && (
        <div className="zone-maker-container">
          <Canvas
            canvasRef={canvasRef}
            mapImageImage={mapImageImage}
            imageLoaded={imageLoaded}
            imageTransparency={imageTransparency}
            canvas={canvas}
            setCanvas={setCanvas}
            setImageDimensions={setImageDimensions}
            handleCanvasClick={handleCanvasClick}
            undoLastPoint={undoLastPoint}
            snapToGrid={snapToGrid}
            gridStep={gridStep}
          />
          <div className="zone-maker-controls" ref={zonesContainerRef}>
            <Slider
              imageTransparency={imageTransparency}
              handleImageTransparency={handleImageTransparency}
            />
            <div className="zone-maker-snap-controls" style={{ maxWidth: '95%', marginTop: '12px' }}>
              <Checkbox
                checked={snapToGrid}
                onChange={(e) => {
                  setSnapToGrid(e.target.checked);
                  localStorageService.set('ZONE_MAKER_SNAP_TO_GRID', e.target.checked);
                }}
              >
                Snap to grid
              </Checkbox>

              <div className="zone-maker-grid-step">
                <span style={{ display: 'block', marginBottom: 4 }}>
                  Grid size (px):
                </span>
                <InputNumber
                  min={2}
                  max={20}
                  step={1}
                  value={gridStep}
                  onChange={(val) => {
                    if (typeof val === 'number') {
                      setGridStep(val);
                      localStorageService.set('ZONE_MAKER_GRID_STEP', val);
                    }
                  }}
                  disabled={!snapToGrid}
                  style={{ width: '70px' }}
                />
              </div>
            </div>
            <Form
              formik={formik}
              fieldArrayRef={fieldArrayRef}
              isDrawing={isDrawing}
              activeZoneIndex={activeZoneIndex}
              drawnPolygons={drawnPolygons}
              startDrawing={startDrawing}
              deleteZonePolygon={deleteZonePolygon}
              cancelDrawing={cancelDrawing}
              setDeleteModalVisible={setDeleteModalVisible}
              setDeleteTarget={setDeleteTarget}
              editingZoneIndex={editingZoneIndex}
              startEditing={enablePolygonEditing}
              cancelEditing={cancelPolygonEditing}
              saveEditing={savePolygonEdits}
              handlePolygonColorChange={handlePolygonColorChange}
              currentZonesFilter={currentZonesFilter}
              searchZonesValue={searchZonesValue}
              startInsertingRectangle={startInsertingRectangle}
              isInsertingRectangle={isInsertingRectangle}
              cancelInsertingRectangle={cancelInsertingRectangle}
              saveInsertingRectangle={saveInsertingRectangle}
            />
          </div>
          <DeleteModal
            text="zone"
            modalVisible={deleteModalVisible}
            onCancel={() => {
              setDeleteModalVisible(false);
              setDeleteTarget(null);
            }}
            onConfirm={handleZoneDelete}
          />
          {
            insertLeavePageModal && <LeavePageModal />
          }
        </div>
      )}
    </>
  );
});

ZoneMaker.displayName = 'ZoneMaker';

export default memo(ZoneMaker);
